import { Location } from 'history';
import { Dispatch } from 'redux';
import { LocalStorageService } from '@mm/utils';
import { SlideOutMenuType } from '../interfaces/view';
import AdConfigModel from '../model/AdConfigModel';
import { CtmAdModel } from '../model/CtmAdModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import Actions from './actions';
import usersCRUDActions from './UsersCRUDActions';

export const setRequestDemoSnoozeDateActionCreator = (
  requestDemoSnoozeDate: string,
) => {
  LocalStorageService.setItem('requestDemoSnoozeDate', requestDemoSnoozeDate);

  return {
    type: Actions.SET_REQUEST_DEMO_SNOOZE_DATE,
    requestDemoSnoozeDate,
  };
};

export function activateChatbotEditor(chatbotId: number, userId: number) {
  return (dispatch: Dispatch<StoreState>) => {
    dispatch(usersCRUDActions.updateActiveChatbotId(userId, chatbotId));
    dispatch({
      type: Actions.ACTIVATE_CHATBOT_EDITOR,
      chatbotId,
    });
  };
}

export function activatePageEditor(pageId: number, keepMainMenu?: boolean) {
  // Automatically request the widgets for this page when we activate the editor
  return (dispatch: Dispatch<StoreState>) => {
    dispatch({
      type: Actions.ACTIVATE_PAGE_EDITOR,
      pageId,
    });
    if (!keepMainMenu) {
      dispatch({
        type: Actions.UNSELECT_MAIN_MENU,
      });
    }
  };
}

export function deactivatePageEditor() {
  return (dispatch: Dispatch<StoreState>) => {
    dispatch({
      type: Actions.DEACTIVATE_PAGE_EDITOR,
    });
  };
}

export function showConfirmDeleteChatbot(chatbotId: number, name) {
  return {
    type: Actions.SHOW_CONFIRM_DELETE_CHATBOT,
    chatbotId,
    name,
  };
}

export function hideConfirmDeleteChatbot() {
  return {
    type: Actions.HIDE_CONFIRM_DELETE_CHATBOT,
  };
}

export function showConfirmDeletePageGroup(
  pageGroupId: number,
  groupName?: string,
) {
  return {
    type: Actions.SHOW_CONFIRM_DELETE_PAGE_GROUP,
    pageGroupId,
    groupName,
  };
}

export function hideConfirmDeletePageGroup() {
  return {
    type: Actions.HIDE_CONFIRM_DELETE_PAGE_GROUP,
  };
}

export function showConfirmDeletePage(pageId: number, groupPages) {
  return {
    type: Actions.SHOW_CONFIRM_DELETE_PAGE,
    pageId,
    groupPages,
  };
}

export function hideConfirmDeletePage() {
  return {
    type: Actions.HIDE_CONFIRM_DELETE_PAGE,
  };
}

export function activatePageEditorFormField(formFieldId: number) {
  return {
    type: Actions.ACTIVATE_PAGE_EDITOR_FORM_FIELD,
    formFieldId,
  };
}

export function activatePropertiesPane(activeKey) {
  return {
    type: Actions.ACTIVATE_PROPERTIES_PANE,
    activeKey,
  };
}

export function showDeleteErrorModal(message: string) {
  return {
    type: Actions.SHOW_DELETE_ERROR_MODAL,
    payload: {
      message,
    },
  };
}

export function hideDeleteErrorModal() {
  return {
    type: Actions.HIDE_DELETE_ERROR_MODAL,
  };
}

export const showSlideoutMenu = (
  menuType: SlideOutMenuType,
  menuProps: {} = {},
) => ({
  type: Actions.SHOW_SLIDEOUT_MENU,
  menuType,
  menuProps,
});

export const hideSlideoutMenu = () => ({
  type: Actions.HIDE_SLIDEOUT_MENU,
});

export const showChecklistTooltip = () => ({
  type: Actions.SHOW_CHECKLIST_TOOLTIP,
});

export const hideChecklistTooltip = () => ({
  type: Actions.HIDE_CHECKLIST_TOOLTIP,
});

export const showCreateBotModal = () => ({
  type: Actions.SHOW_CREATE_BOT_MODAL,
});

export const hideCreateBotModal = () => ({
  type: Actions.HIDE_CREATE_BOT_MODAL,
});

export const selectMainMenu = () => ({ type: Actions.SELECT_MAIN_MENU });

export const unselectMainMenu = () => ({ type: Actions.UNSELECT_MAIN_MENU });

export const selectCtmAd = (entity: CtmAdModel) => ({
  type: Actions.CTM_ADS_SELECT_AD,
  entity,
});

export const unselectCtmAd = () => ({
  type: Actions.CTM_ADS_UNSELECT_AD,
});

export const selectAdConfig = (entity: AdConfigModel) => ({
  type: Actions.AD_CONFIGS_SELECT_AD,
  entity,
});

export const unselectAdConfig = () => ({
  type: Actions.AD_CONFIGS_UNSELECT_AD,
});

export const selectMessengerAd = entity => ({
  type: Actions.MESSENGER_ADS_SELECT_AD,
  entity,
});

export const unselectMessengerAd = () => ({
  type: Actions.MESSENGER_ADS_UNSELECT_AD,
});

export const selectRssFeed = entity => ({
  type: Actions.RSS_FEEDS_SELECT_FEED,
  entity,
});

export const unselectRssFeed = () => ({
  type: Actions.RSS_FEEDS_UNSELECT_FEED,
});

export const selectLandingPage = entity => ({
  type: Actions.LANDING_PAGES_SELECT_ENTITY,
  entity,
});

export const unselectLandingPage = () => ({
  type: Actions.LANDING_PAGES_UNSELECT_ENTITY,
});

export const setDashboardPaginationPage = page => ({
  type: Actions.SET_DASHBOARD_PAGINATION_PAGE,
  page,
});

export const appendAppNavigationHistory = (location: Location) => ({
  type: Actions.APPEND_APP_NAVIGATION_HISTORY,
  location,
});

export const setLandmineOpenId = (landlandmineOpenId?: number) => ({
  type: Actions.SET_LANDMINE_OPEN_ID,
  landlandmineOpenId,
});

export const setSuppressEmbeddedChatWidget = (
  suppressEmbeddedChatWidget: boolean,
) => ({
  type: Actions.SET_SUPPRESS_BOT_WIDGET,
  suppressEmbeddedChatWidget,
});

export const showBetaSubNav = (subNavName: string) => ({
  type: Actions.VIEW_SHOW_BETA_SUBNAV,
  subNavName,
});

export const hideBetaSubNav = (subNavName: string) => ({
  type: Actions.VIEW_HIDE_BETA_SUBNAV,
  subNavName,
});

export const disableMainScroll = () => ({
  type: Actions.DISABLE_MAIN_SCROLL,
});
export const enableMainScroll = () => ({
  type: Actions.ENABLE_MAIN_SCROLL,
});

export const setWidgetToFocus = (id?: number) => ({
  type: Actions.SET_WIDGET_TO_FOCUS,
  id,
});

const viewActions = {
  activateChatbotEditor,
  activatePageEditor,
  appendAppNavigationHistory,
  setLandmineOpenId,
  deactivatePageEditor,
  showConfirmDeleteChatbot,
  hideConfirmDeleteChatbot,
  showConfirmDeletePageGroup,
  hideConfirmDeletePageGroup,
  showConfirmDeletePage,
  hideConfirmDeletePage,
  activatePageEditorFormField,
  activatePropertiesPane,
  showDeleteErrorModal,
  hideDeleteErrorModal,
  showCreateBotModal,
  hideCreateBotModal,
  selectMainMenu,
  unselectMainMenu,
  selectMessengerAd,
  unselectMessengerAd,
  selectRssFeed,
  unselectRssFeed,
  selectLandingPage,
  unselectLandingPage,
  setDashboardPaginationPage,
  setSuppressEmbeddedChatWidget,
  showBetaSubNav,
  hideBetaSubNav,
  disableMainScroll,
  enableMainScroll,
  hideSlideoutMenu,
  showSlideoutMenu,
  setWidgetToFocus,
};

export default viewActions;

export const useViewActions = getActionHookBuilder(viewActions, [
  'activatePageEditor',
  'activateChatbotEditor',
  'disableMainScroll',
  'enableMainScroll',
  'hideBetaSubNav',
  'hideSlideoutMenu',
  'selectMainMenu',
  'setLandmineOpenId',
  'setSuppressEmbeddedChatWidget',
  'showBetaSubNav',
  'showConfirmDeletePage',
  'showConfirmDeletePageGroup',
  'showSlideoutMenu',
  'unselectMainMenu',
]);
